import React from "react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";

import Spinner from "@/components/atoms/Spinner";

export const buttonVariants = cva(
  "inline-flex shrink-0 items-center truncate gap-x-2 justify-center font-medium focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-input disabled:opacity-40 disabled:pointer-events-none transition duration-75 ",
  {
    variants: {
      size: {
        "3xs": "px-2 py-1 text-xs rounded",
        "2xs": "px-3.5 py-2 text-xs rounded",
        xs: "px-3.5 py-2 text-sm rounded-md",
        sm: "px-4 py-2.5 text-sm rounded-md",
        md: "px-4 py-2.5 text-base rounded-lg",
        lg: "px-5 py-3 text-base rounded-lg",
        xl: "px-7 py-4 text-xl rounded-lg",
      },
      color: {
        brand: "",
        natural: "",
        error: "",
      },
      variants: {
        primary: "shadow-xs hover:shadow-sm",
        secondary: "shadow-xs hover:shadow-sm",
        outline: "border",
        text: "",
        textLight: "",
      },
    },
    defaultVariants: {
      size: "sm",
      color: "brand",
      variants: "primary",
    },
    compoundVariants: [
      {
        color: "brand",
        variants: "primary",
        class:
          "bg-brand-primary-solid text-primary-brand hover:bg-brand-primary-solid-hover active:bg-brand-primary-solid-pressed",
      },
      {
        color: "brand",
        variants: "secondary",
        class:
          "bg-brand-primary text-brand-primary hover:bg-brand-primary-hover hover:text-brand-primary-hover active:bg-brand-primary-pressed active:text-brand-primary-pressed",
      },
      {
        color: "brand",
        variants: "outline",
        class:
          "border-brand-primary text-brand-primary hover:text-brand-primary-hover active:text-brand-primary-pressed",
      },
      {
        color: "brand",
        variants: "text",
        class: "bg-transparent text-brand-primary hover:text-brand-primary-hover active:text-brand-primary-pressed",
      },
      {
        color: "natural",
        variants: "primary",
        class:
          "bg-secondary hover:bg-secondary-hover text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed",
      },
      {
        color: "natural",
        variants: "secondary",
        class:
          "bg-primary hover:bg-primary-hover active:bg-primary active:text-tertiary-pressed text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed",
      },
      {
        color: "natural",
        variants: "outline",
        class: "border-primary bg-white text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed",
      },
      {
        color: "natural",
        variants: "text",
        class: "text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed",
      },
      {
        color: "natural",
        variants: "textLight",
        class: "text-quinary hover:text-quinary-hover active:text-quinary-pressed",
      },
      {
        color: "error",
        variants: "primary",
        class: "bg-error-solid text-primary-brand hover:bg-error-solid-hover active:bg-error-solid-pressed",
      },
      {
        color: "error",
        variants: "secondary",
        class:
          "bg-error-primary text-error hover:bg-error-primary-hover hover:text-error-hover active:bg-error-primary-pressed active:text-error-pressed",
      },
      {
        color: "error",
        variants: "outline",
        class: "border-error text-error bg-transparent hover:text-error-hover active:text-error-pressed",
      },
      {
        color: "error",
        variants: "text",
        class: "bg-transparent text-error hover:text-error-hover active:text-error-pressed",
      },
    ],
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    label?: React.ReactNode | string;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    isLoading?: boolean;
  };

const Button: React.FC<ButtonProps> = ({
  size,
  color,
  variants,
  disabled,
  leadingIcon,
  trailingIcon,
  label,
  className,
  isLoading,
  ...props
}) => {
  return (
    <button
      className={clsx(buttonVariants({ size, color, variants }), className)}
      type="button"
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Spinner size={"2xs"} />}
      {leadingIcon && !isLoading && <span>{leadingIcon}</span>}
      {label && <span>{label}</span>}
      {trailingIcon && <span>{trailingIcon}</span>}
    </button>
  );
};

export default Button;
